import React, { useState, useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./index.css";

import { submitGameScore } from "../../../../api-requests/game";

/*
 * DropPinGame
 * A geography mini-game shown while the CV upload is processing.
 * Users tap a real Med map (Leaflet + CartoDB tiles, no labels) to
 * place a pin for each prompted yacht hub. Scored by distance.
 * On game over the score is POSTed to the leaderboard and the
 * server's percentile is shown. Falls back to a synthetic curve
 * if the API call fails.
 */

const PORTS = [
    { name: "Monaco",            lat: 43.7384, lng: 7.4246 },
    { name: "Marseille",         lat: 43.2965, lng: 5.3698 },
    { name: "St Tropez",         lat: 43.2727, lng: 6.6407 },
    { name: "Palma de Mallorca", lat: 39.5696, lng: 2.6502 },
    { name: "Ibiza",             lat: 38.9067, lng: 1.4206 },
    { name: "Barcelona",         lat: 41.3851, lng: 2.1734 },
    { name: "Portofino",         lat: 44.3033, lng: 9.2102 },
    { name: "Capri",             lat: 40.5532, lng: 14.2222 },
    { name: "Porto Cervo",       lat: 41.1349, lng: 9.5392 },
    { name: "Palermo",           lat: 38.1157, lng: 13.3613 },
    { name: "Dubrovnik",         lat: 42.6507, lng: 18.0944 },
    { name: "Corfu",             lat: 39.6243, lng: 19.9217 },
    { name: "Mykonos",           lat: 37.4467, lng: 25.3289 },
    { name: "Santorini",         lat: 36.3932, lng: 25.4615 },
    { name: "Bodrum",            lat: 37.0317, lng: 27.4305 },
];

const MAX_SCORE = PORTS.length * 100;

// Synthetic fallback for percentile if the leaderboard endpoint fails.
const PERCENTILE_TABLE = [
    [0, 1], [400, 6], [600, 15], [800, 28], [1000, 45],
    [1150, 62], [1280, 78], [1380, 90], [1460, 96], [1500, 99],
];

const syntheticPercentile = (score) => {
    if (score >= MAX_SCORE) return 99;
    for (let i = 1; i < PERCENTILE_TABLE.length; i++) {
        if (score <= PERCENTILE_TABLE[i][0]) {
            const [x0, y0] = PERCENTILE_TABLE[i - 1];
            const [x1, y1] = PERCENTILE_TABLE[i];
            const t = (score - x0) / (x1 - x0);
            return Math.round(y0 + t * (y1 - y0));
        }
    }
    return 99;
};

const rankLabel = (pct) => {
    if (pct >= 90) return "You must be a Captain";
    if (pct >= 70) return "You must be a First Mate";
    if (pct >= 45) return "You must be a Bosun";
    if (pct >= 20) return "You must be an Engineer";
    return "Welcome to your first charter";
};

const scoreForDistanceKm = (km) => {
    if (km <= 60)  return { points: 100, label: "Bullseye",  tier: "great" };
    if (km <= 150) return { points: 85,  label: "Excellent", tier: "great" };
    if (km <= 300) return { points: 65,  label: "Close",     tier: "ok" };
    if (km <= 600) return { points: 40,  label: "Off",       tier: "miss" };
    return { points: 20, label: "Adrift", tier: "miss" };
};

const shuffle = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
};

const userIcon = L.divIcon({
    className: "drop-pin-marker drop-pin-marker--user",
    html:
        '<svg viewBox="0 0 24 32" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
            '<path d="M12 0 C5.4 0 0 5.4 0 12 C0 21 12 32 12 32 S24 21 24 12 C24 5.4 18.6 0 12 0 Z" fill="#FF6B47" stroke="#FFFFFF" stroke-width="2"/>' +
            '<circle cx="12" cy="12" r="4" fill="#FFFFFF"/>' +
        '</svg>',
    iconSize: [28, 36],
    iconAnchor: [14, 36],
});

const targetIcon = L.divIcon({
    className: "drop-pin-marker drop-pin-marker--target",
    html:
        '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
            '<circle cx="12" cy="12" r="10" fill="#2E7D52" stroke="#FFFFFF" stroke-width="2"/>' +
            '<circle cx="12" cy="12" r="6" fill="none" stroke="#FFFFFF" stroke-width="1.5"/>' +
            '<circle cx="12" cy="12" r="2" fill="#FFFFFF"/>' +
        '</svg>',
    iconSize: [24, 24],
    iconAnchor: [12, 12],
});

function EndScreen({ score, bestStreak, totalRounds, profileReady, onContinue, leaderboard }) {

    const pct = leaderboard && typeof leaderboard.percentile === "number"
        ? leaderboard.percentile
        : syntheticPercentile(score);
    const rank = rankLabel(pct);

    return (
        <div className="drop-pin-end">
            <div className="drop-pin-end-eyebrow">Final Score</div>
            <h2 className="drop-pin-end-title">{rank}</h2>

            <div className="drop-pin-end-score">{score}</div>
            <p className="drop-pin-end-score-cap">out of {totalRounds * 100}</p>

            <div className="drop-pin-end-rankrow">
                <div className="drop-pin-end-rank-label">Your rank</div>
                <p className="drop-pin-end-rank-title">
                    Better than <strong>{pct}%</strong> of crew
                </p>
                <div className="drop-pin-end-bar">
                    <div className="drop-pin-end-bar-fill" style={{ width: pct + "%" }} />
                    <div className="drop-pin-end-bar-marker" style={{ left: pct + "%" }} />
                </div>
                {leaderboard && leaderboard.totalPlayers > 1 && (
                    <p className="drop-pin-end-rank-foot">{leaderboard.totalPlayers.toLocaleString()} crew have played</p>
                )}
            </div>

            <div className="drop-pin-end-meta">
                <div className="drop-pin-end-meta-cell">
                    <span className="drop-pin-end-meta-label">Best streak</span>
                    <span className="drop-pin-end-meta-value">{bestStreak}</span>
                </div>
                <div className="drop-pin-end-meta-cell">
                    <span className="drop-pin-end-meta-label">Ports</span>
                    <span className="drop-pin-end-meta-value">{totalRounds}</span>
                </div>
            </div>

            <button
                type="button"
                className="drop-pin-end-cta"
                disabled={!profileReady}
                onClick={onContinue}
            >
                {profileReady
                    ? "Continue to next step →"
                    : (<><span className="drop-pin-end-cta-spinner" />Still reading your CV…</>)
                }
            </button>
        </div>
    );

}

function DropPinGame({ profileReady, onContinue }) {

    const mapDivRef = useRef(null);
    const mapRef = useRef(null);
    const markersRef = useRef({ user: null, target: null, line: null });
    const handleClickRef = useRef(null);
    const submittedRef = useRef(false);

    const [queue] = useState(() => shuffle(PORTS.map((_, i) => i)));
    const [round, setRound] = useState(0);
    const [pendingPin, setPendingPin] = useState(null);
    const [feedback, setFeedback] = useState(null);
    const [score, setScore] = useState(0);
    const [streak, setStreak] = useState(0);
    const [bestStreak, setBestStreak] = useState(0);
    const [gameOver, setGameOver] = useState(false);
    const [leaderboard, setLeaderboard] = useState(null);

    const clearOverlay = () => {
        const map = mapRef.current;
        if (!map) return;
        ["user", "target", "line"].forEach((k) => {
            if (markersRef.current[k]) {
                map.removeLayer(markersRef.current[k]);
                markersRef.current[k] = null;
            }
        });
    };

    const advance = () => {
        clearOverlay();
        setFeedback(null);
        setPendingPin(null);
        if (round + 1 >= queue.length) {
            setGameOver(true);
        } else {
            setRound(round + 1);
        }
    };

    handleClickRef.current = (e) => {
        if (feedback || gameOver) return;
        const map = mapRef.current;
        if (!map) return;
        if (markersRef.current.user) {
            markersRef.current.user.setLatLng(e.latlng);
        } else {
            markersRef.current.user = L.marker(e.latlng, { icon: userIcon, interactive: false }).addTo(map);
        }
        setPendingPin(e.latlng);
    };

    const handleDropPin = () => {
        if (!pendingPin || feedback || gameOver) return;
        const map = mapRef.current;
        if (!map) return;
        const port = PORTS[queue[round]];
        const portLatLng = L.latLng(port.lat, port.lng);
        const meters = pendingPin.distanceTo(portLatLng);
        const km = meters / 1000;
        const result = scoreForDistanceKm(km);
        result.km = km;

        markersRef.current.target = L.marker(portLatLng, { icon: targetIcon, interactive: false }).addTo(map);
        markersRef.current.line = L.polyline([pendingPin, portLatLng], {
            color: "#FF6B47", weight: 2, dashArray: "4 4", interactive: false,
        }).addTo(map);

        setFeedback(result);
        setScore((s) => s + result.points);
        if (result.tier === "great" || result.tier === "ok") {
            const next = streak + 1;
            setStreak(next);
            setBestStreak((b) => Math.max(b, next));
        } else {
            setStreak(0);
        }
        setTimeout(advance, 1700);
    };

    useEffect(() => {
        if (mapRef.current) return;
        const map = L.map(mapDivRef.current, {
            zoomControl: false,
            scrollWheelZoom: false,
            doubleClickZoom: false,
            boxZoom: false,
            dragging: false,
            touchZoom: false,
            keyboard: false,
            attributionControl: true,
        });
        L.tileLayer("https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png", {
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
            subdomains: "abcd",
            maxZoom: 19,
        }).addTo(map);
        const bounds = L.latLngBounds([34.5, -2], [46, 29.5]);
        map.fitBounds(bounds, { padding: [8, 8] });
        map.setMaxBounds(bounds.pad(0.2));
        map.on("click", (e) => handleClickRef.current && handleClickRef.current(e));
        mapRef.current = map;
        return () => {
            if (mapRef.current) {
                mapRef.current.remove();
                mapRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (!gameOver || submittedRef.current) return;
        submittedRef.current = true;
        const finalRounds = round + (feedback ? 1 : 0);
        submitGameScore({
            gameType: "drop-pin-med",
            score,
            maxScore: MAX_SCORE,
            bestStreak,
            roundsPlayed: finalRounds,
        }).then((res) => {
            if (res && res.status === 201 && res.body && typeof res.body.percentile === "number") {
                setLeaderboard(res.body);
            }
        });
    }, [gameOver]); // eslint-disable-line react-hooks/exhaustive-deps

    if (gameOver) {
        return (
            <EndScreen
                score={score}
                bestStreak={bestStreak}
                totalRounds={queue.length}
                profileReady={profileReady}
                onContinue={onContinue}
                leaderboard={leaderboard}
            />
        );
    }

    const port = PORTS[queue[round]];

    return (
        <div className="drop-pin-game">
            <div className="drop-pin-eyebrow">
                <span className="drop-pin-eyebrow-label">While we read your CV</span>
                <span className="drop-pin-eyebrow-title">Drop the Pin · Mediterranean</span>
            </div>

            <div className="drop-pin-scoreboard">
                <div className="drop-pin-stat">
                    <span className="drop-pin-stat-label">Score</span>
                    <span className="drop-pin-stat-value">{score}</span>
                </div>
                <div className="drop-pin-stat drop-pin-stat--center">
                    <span className="drop-pin-stat-label">Port</span>
                    <span className="drop-pin-stat-value">{round + 1} / {queue.length}</span>
                </div>
                <div className="drop-pin-stat drop-pin-stat--right">
                    <span className="drop-pin-stat-label">Streak</span>
                    <span className="drop-pin-stat-value">
                        {streak}
                        {bestStreak > 0 && <span className="drop-pin-stat-best"> · best {bestStreak}</span>}
                    </span>
                </div>
            </div>

            <div className="drop-pin-prompt">
                <span className="drop-pin-prompt-label">Drop pin on</span>
                <span className="drop-pin-prompt-city">{port.name}</span>
            </div>

            <div className={"drop-pin-mapwrap" + (feedback ? " drop-pin-mapwrap--revealed" : "")}>
                <div ref={mapDivRef} className="drop-pin-map" />
                {feedback && (
                    <div className={"drop-pin-result drop-pin-result--" + feedback.tier}>
                        <span className="drop-pin-result-label">{feedback.label}</span>
                        <span className="drop-pin-result-points">+{feedback.points}</span>
                        <span className="drop-pin-result-distance">{Math.round(feedback.km)} km off</span>
                    </div>
                )}
            </div>

            {pendingPin && !feedback && (
                <button
                    type="button"
                    className="drop-pin-confirm"
                    onClick={handleDropPin}
                >
                    Drop pin here
                </button>
            )}

            <div className="drop-pin-foot">
                <span>
                    {feedback
                        ? "Loading next port…"
                        : pendingPin
                            ? "Tap elsewhere to reposition"
                            : "Tap the map to place your pin"}
                </span>
                <button type="button" className="drop-pin-foot-end" onClick={() => setGameOver(true)}>
                    End game
                </button>
            </div>
        </div>
    );

}

export default DropPinGame;
